import { useEffect, useMemo, useState } from 'react';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useQuery } from '@tanstack/react-query';
import { CustomUser } from 'api/models/CustomUser';
import { ApiService } from 'api/services/ApiService';
import { useZoom } from 'context/ZoomContext';
import { useDebouncedValue } from 'hooks';
import { useLocalStorage } from 'services/hooks';
import { displayErrorMessage } from 'utilities/displayErrorMessage';
import { getInitials } from 'utilities/getInitials';
import { ModelFiltersProps } from './filters.props';

const SELECTED_USER_IDS_KEY = 'modelsWorkboardSelectedUserIds';
const SELECTED_USERS_MAP_KEY = 'modelsWorkboardSelectedUsersMap';

interface UserMap {
  [userId: number]: {
    id: number;
    fullName: string;
  };
}

export const ModelFilters = ({ statusOptions, statusOptionsLoading, onFiltersChange }: ModelFiltersProps) => {
  const [searchParam, setSearchParam] = useState('');
  const [searchStatus, setSearchStatus] = useState('default');
  const [fromDueDate, setFromDueDate] = useState<Date | null>(null);
  const [toDueDate, setToDueDate] = useState<Date | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<CustomUser[]>([]);
  const [userSearchInput, setUserSearchInput] = useState('');

  const [storedUserIds, setStoredUserIds] = useLocalStorage<number[]>(SELECTED_USER_IDS_KEY, []);
  const [storedUsersMap, setStoredUsersMap] = useLocalStorage<UserMap>(SELECTED_USERS_MAP_KEY, {});

  const debouncedSearchParam = useDebouncedValue(searchParam, 500);
  const debouncedUserSearch = useDebouncedValue(userSearchInput, 500);

  const { zoomLevel } = useZoom();

  // Fetch users for autocomplete
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users-list', debouncedUserSearch],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList(debouncedUserSearch || undefined);
      const users = (response as CustomUser[]) || [];
      return users
    },
    enabled: true,
  });

  const sortedUsers = useMemo(() => {
    const users = usersData || [];
    const searchResultIds = new Set(users.map(u => u.id).filter(Boolean));
    
    const selectedUsersNotInResults = selectedUsers.filter(
      selected => selected.id && !searchResultIds.has(selected.id)
    );
    
    const allUsers = [...selectedUsersNotInResults, ...users];
    
    const uniqueUsers = Array.from(
      new Map(allUsers.map(user => [user.id, user])).values()
    );
    
    return uniqueUsers.sort((a, b) => {
      const aSelected = selectedUsers.some(selected => selected.id === a.id);
      const bSelected = selectedUsers.some(selected => selected.id === b.id);
      
      if (aSelected && !bSelected) {
        return -1;
      }
      if (!aSelected && bSelected) {
        return 1;
      }
      return 0;
    });
  }, [usersData, selectedUsers]);

  // Restore selectedUsers from stored mapping on mount
  useEffect(() => {
    if (
      Array.isArray(storedUserIds) &&
      storedUserIds.length > 0 &&
      selectedUsers.length === 0 &&
      Object.keys(storedUsersMap).length > 0
    ) {
      const restoredUsers: CustomUser[] = storedUserIds
        .map(userId => {
          const userData = storedUsersMap[userId];
          if (userData) {
            // Split fullName back into first_name and last_name for display
            const nameParts = userData.fullName.split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';
            return {
              id: userData.id,
              first_name: firstName,
              last_name: lastName,
              is_staff: true,
            } as CustomUser;
          }
          return null;
        })
        .filter((user): user is CustomUser => user !== null);

      if (restoredUsers.length > 0) {
        setSelectedUsers(restoredUsers);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  useEffect(() => {
    const userIds = selectedUsers.map(user => user.id).filter((id): id is number => id !== undefined);

    // Update stored user IDs and mapping when selectedUsers changes
    setStoredUserIds(userIds);

    // Create mapping of userId -> user data for restoration (only id and fullName)
    const usersMap: UserMap = {};
    selectedUsers.forEach(user => {
      if (user.id) {
        const fullName =
          [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || user.username || '';
        usersMap[user.id] = {
          id: user.id,
          fullName,
        };
      }
    });
    setStoredUsersMap(usersMap);

    // Pass userIds to the search
    onFiltersChange({
      searchParam: debouncedSearchParam,
      searchStatus,
      fromDate: fromDueDate,
      toDate: toDueDate,
      userIds,
    });
  }, [
    debouncedSearchParam,
    searchStatus,
    fromDueDate,
    toDueDate,
    selectedUsers,
    onFiltersChange,
    setStoredUserIds,
    setStoredUsersMap,
  ]);

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleSearchStatusChange = (event: SelectChangeEvent<string>) => {
    setSearchStatus(event.target.value);
  };

  const handleFromDueDateChange = (date: Date | null) => {
    setFromDueDate(date);
  };

  const handleToDueDateChange = (date: Date | null) => {
    if (fromDueDate && date && fromDueDate > date) {
      displayErrorMessage('From Valuation Date must be before To Valuation Date');
      return;
    }
    setToDueDate(date);
  };

  const handleUsersChange = (_event: unknown, newValue: CustomUser[]) => {
    setSelectedUsers(newValue);
  };

  const cleanFilters = () => {
    setSearchParam('');
    setSearchStatus('');
    setFromDueDate(null);
    setToDueDate(null);
    setSelectedUsers([]);
    setUserSearchInput('');
    // Clear selected user IDs and mapping from localStorage
    setStoredUserIds([]);
    setStoredUsersMap({});
  };

  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ marginTop: 2, marginLeft: 2 }}>
      <TextField label="Search" variant="outlined" value={searchParam} onChange={handleSearchParamChange} />
      <Select
        label="Status"
        variant="outlined"
        value={searchStatus}
        onChange={handleSearchStatusChange}
        sx={{ width: 200 }}>
        <MenuItem disabled value="default">
          Select Status
        </MenuItem>
        <MenuItem value="All">All</MenuItem>
        {statusOptionsLoading ? (
          <MenuItem disabled> Loading... </MenuItem>
        ) : (
          statusOptions.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))
        )}
      </Select>
      <DatePicker
        label="From Due Date"
        value={fromDueDate}
        onChange={handleFromDueDateChange}
        slotProps={{
          textField: {
            variant: 'outlined',
          },
        }}
      />
      <DatePicker
        label="To Due Date"
        value={toDueDate}
        onChange={handleToDueDateChange}
        minDate={fromDueDate || undefined}
        slotProps={{
          textField: {
            variant: 'outlined',
          },
        }}
      />
      <Autocomplete
        multiple
        options={sortedUsers}
        getOptionLabel={option => {
          const fullName = [option.first_name, option.last_name].filter(Boolean).join(' ');
          return fullName || option.email || option.username || '';
        }}
        slotProps={{
          popper: {
            sx: {
              width: `${zoomLevel * 300}px !important`,
            },
          },
        }}
        disableCloseOnSelect
        value={selectedUsers}
        onChange={handleUsersChange}
        onInputChange={(_event, newInputValue) => {
          setUserSearchInput(newInputValue);
        }}
        inputValue={userSearchInput}
        loading={isLoadingUsers}
        renderInput={params => (
          <TextField {...params} label="Select Users" variant="outlined" placeholder="Search for users..." sx={{ maxWidth: 300 }} />
        )}
        renderTags={value => (
          <Box 
            sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 0.5,
              maxHeight: 100,
              alignItems: 'flex-start',
              width: '100%',
              py: 0.5
            }}
          >
            {value.map(option => <Avatar key={option.id} sx={{ width: 32, height: 32 }}>
              {getInitials({ name: `${option.first_name  } ${  option.last_name}` })}
            </Avatar>)}
          </Box>
        )}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <li key={option.id} {...optionProps} style={{ padding: 0 }}>
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              {[option.first_name, option.last_name].join(' ') || option.email || option.username || ''}
            </li>
          );
        }}
        sx={{ minWidth: 300 }}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      <Tooltip title="Clear filters">
        <IconButton onClick={cleanFilters}>
          <FilterAltOffIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
